// Founding 100 offer (2026-10-11; yearly-only from 2026-10-17, Szymon): on a yearly plan the whole
// first year costs the same as 6 months of the plan's monthly price (Cadet $29 -> $174). No
// founding discount on monthly billing. For the first 100 customers, until Nov 1, 2026 00:00
// America/New_York (end of Oct 31), plus free done-for-you setup.
//
// ONE place for every trace of the offer on the site: the header strip (components/Header.vue),
// the founding prices, note and `promo` links on the plan cards of / and /pricing
// (components/PlanCards.vue), the team/freelancer/agency offer sections, the trial dialog line,
// and the removals in scripts/founding-expiry.mjs (llms.txt, two blog posts, two FAQ copies).
//
// IT SWITCHES OFF BY ITSELF at the deadline (A9, legal-protection release), no human action:
//   1. `FOUNDING_OFFER.enabled` is false in any build that starts at or after the deadline, and
//      scripts/build-site.mjs (the Netlify build command) then also applies the removals and
//      refreshes the translation caches, so that build passes.
//   2. netlify/functions/founding-offer-rebuild.mts starts that build at the deadline.
//   3. Until it finishes, pages built earlier hide the offer on the client (useFoundingOffer) and
//      netlify/edge-functions/founding-offer-expiry.ts hides it in the HTML and strips llms.txt.
//   4. After that rebuild is live, scripts/verify-founding-cut-live.mjs checks the live site (all
//      languages) for any trace of the offer and for the legal and Lifetime wording that stays.
// To end it earlier, set FOUNDING_OFFER_SWITCH to false and run the translation refresh below.
// The header strip then shows its usual copy again. The strip is on every page, so flipping the
// switch changes the rendered copy and the translation caches must follow before `pnpm generate`
// passes. Every string involved is hand-translated in data/localized-copy.ts, so no translation
// service runs:
//
//   export SERVER_PRESET=netlify
//   SKIP_LOCALIZED_EDITIONS=1 pnpm generate
//   pnpm i18n:extract
//   node --experimental-strip-types scripts/seed-chrome-translations.mjs
//   pnpm i18n:translate        # reports "0 missing" per locale and only refreshes the hashes
//   pnpm generate
//
// The discount itself lives in Stripe: customers use one code, FOUNDING100, which the API swaps for
// the plan's own amount-off yearly code and refuses on any non-yearly price (API #660, app #526),
// only for accounts that never had a plan. The `promo` parameter on our sign-up links is informational.
export const FOUNDING_OFFER_SWITCH = true;

/**
 * The English offer strings that the / and /pricing locale dictionaries (data/home-i18n/*.json)
 * translate. A build without the offer drops exactly these entries from the dictionary it loads
 * (composables/useHomeI18n.ts), so no localized payload carries them after the deadline. Listed
 * by hand rather than matched by a word, so a later non-offer string can't be dropped by accident;
 * tests/tracking/foundingOffer.test.ts checks every entry is still a dictionary key.
 */
export const FOUNDING_OFFER_DICTIONARY_KEYS: readonly string[] = [
  "Yearly plans only: your whole first year costs the same as 6 months of the monthly price, for the first 100 customers who join by Oct 31. Send us your website and we set up your workspace for you, free.",
  "Founding member offer",
  "Founding member price",
  "14-day free trial. Card required. Then {foundingTotal} for your first year of {plan}, then {total}/year. Renews automatically every year until you cancel. First 100 customers, ends Oct 31, 2026. Cancel anytime in Settings → Billing.",
  "Founding price on yearly plans: from {price}/mo for your first year ({total} billed yearly), until Oct 31.",
  "The founding offer is on yearly plans only: switch to Yearly and your whole first year costs the same as 6 months of the monthly price. First 100 customers, ends Oct 31.",
];

/**
 * Nov 1, 2026 00:00:00 America/New_York (EDT, UTC-4; daylight time ends later that day), the
 * moment the offer stops. The Stripe coupon's `redeem_by` is the second before.
 */
export const FOUNDING_OFFER_ENDS_AT = '2026-11-01T04:00:00Z';
export const FOUNDING_OFFER_ENDS_AT_MS = Date.parse(FOUNDING_OFFER_ENDS_AT);

/**
 * True once the offer's deadline has passed. Two layers use it, so the offer goes away with no
 * human action:
 *  - BUILD TIME: a build that runs at or after the deadline renders no offer at all (`enabled`
 *    below, `scripts/build-site.mjs`, `scripts/founding-expiry.mjs`, plus the scheduled rebuild in
 *    netlify/functions/founding-offer-rebuild.mts that starts one at the deadline).
 *  - RUNTIME: pages built before the deadline hide the offer on the client the moment it passes
 *    (useFoundingOffer), and the edge function netlify/edge-functions/founding-offer-expiry.ts hides
 *    it in the HTML and strips llms.txt even if no rebuild has happened.
 */
export const isFoundingOfferExpired = (nowMs: number): boolean => nowMs >= FOUNDING_OFFER_ENDS_AT_MS;

/** The pure switch: the manual flag AND the clock. `enabled` below is this at build time. */
export const isFoundingOfferEnabled = (nowMs: number, flag: boolean = FOUNDING_OFFER_SWITCH): boolean =>
  flag && !isFoundingOfferExpired(nowMs);

declare const __W369_BUILD_TIME_MS__: number | undefined;
// nuxt.config.ts defines the build's start time into every bundle, so the server render and the
// client bundle agree (hydration never mismatches) and a visitor's clock never decides what the
// HTML was built with. Outside a Nuxt build (unit tests, scripts) the real clock is used.
export const BUILD_TIME_MS: number =
  typeof __W369_BUILD_TIME_MS__ === 'number' ? __W369_BUILD_TIME_MS__ : Date.now();

/**
 * True in a build that started at or after the deadline, whatever the manual switch says. The
 * free done-for-you setup strip and the /pricing setup line (data/setup-offer.ts) take the
 * offer's place only then, so they never show while the offer could still run.
 */
export const FOUNDING_OFFER_ENDED_AT_BUILD = isFoundingOfferExpired(BUILD_TIME_MS);

export const FOUNDING_OFFER = {
  /** Build-time state: the manual switch AND the deadline not yet reached when the build started. */
  enabled: isFoundingOfferEnabled(BUILD_TIME_MS),
  code: 'FOUNDING100',
  endsAt: FOUNDING_OFFER_ENDS_AT,
  /** Yearly billing only: the first year costs this many months of the monthly list price. */
  firstYearMonths: 6,
  /** The plans with a founding code in Stripe (each capped at 100). Enterprise has none. */
  plans: ['cadet', 'voyager', 'specialist', 'commander'],
} as const;

/** True when the plan has a founding price (Enterprise does not). */
export const hasFoundingPrice = (slug: string): boolean =>
  (FOUNDING_OFFER.plans as readonly string[]).includes(slug);

/** A price string as the pricing data writes it ("74", "23.25", "1,430.99") in cents. */
const toCents = (price: string): number => {
  const cleaned = String(price).replace(/,/g, '').trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(cleaned)) throw new Error(`Not a price: ${price}`);
  const [whole, fraction = ''] = cleaned.split('.');
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
};

/** Cents back into the pricing data's format: "37", "29.62", "1,435.49". */
const fromCents = (cents: number): string => {
  const whole = Math.floor(cents / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const fraction = cents % 100;
  return fraction === 0 ? whole : `${whole}.${String(fraction).padStart(2, '0')}`;
};

/** The founding first-year total for a plan's monthly list price: 6 months of it ("29" -> "174"). */
export const foundingFirstYear = (monthly: string): string =>
  fromCents(toCents(monthly) * FOUNDING_OFFER.firstYearMonths);

/**
 * That first year per month, as the plan cards show it beside the struck yearly price
 * ("29" -> "14.50"): the total over 12, rounded half up to the cent.
 */
export const foundingYearlyMonthly = (monthly: string): string =>
  fromCents(Math.floor((toCents(monthly) * FOUNDING_OFFER.firstYearMonths + 6) / 12));

/**
 * A sign-up link that names the offer: `promo=FOUNDING100` is added (an existing `promo` is
 * kept). Campaign parameters are never added here: the site's own link handling
 * (TrialSignupDialog, conversion-tracking) carries the visitor's consented UTMs, and an ad
 * visitor's tags must not be replaced by ours.
 */
export const foundingSignupUrl = (href: string): string => {
  const url = new URL(href);
  if (!url.searchParams.has('promo')) url.searchParams.set('promo', FOUNDING_OFFER.code);
  return url.toString();
};

/**
 * A plan card's sign-up link: `plan`, the billing period the card shows (`billing=yearly|monthly`,
 * so the app's plan step opens on the price the visitor just read, not always Monthly) and, while
 * the offer runs, the founding promo for plans that have a founding price.
 */
export const planSignupUrl = (slug: string, annual: boolean, foundingEnabled: boolean): string => {
  const url = new URL('https://app.workspace369.com/signup');
  url.searchParams.set('plan', slug);
  url.searchParams.set('billing', annual ? 'yearly' : 'monthly');
  const href = url.toString();
  return foundingEnabled && hasFoundingPrice(slug) ? foundingSignupUrl(href) : href;
};
